/**
 * Where the OIDC session lives between page loads.
 *
 * `sessionStorage`, not `localStorage`: the token is a bearer credential readable by any
 * script on this origin, so it should not outlive the tab that obtained it. Nothing in this
 * module logs a token value.
 */

// `slate.` is the ESTATE storage namespace, not a brand — coil already writes this same key at
// my.app.co.il (same docit-api source), so every existing session survives the appcoil cutover.
// Do not re-prefix or alias this key for a site profile: it would mass-sign-out every
// my.app.co.il session.
const SESSION_KEY = "slate.auth.session";

export interface StoredSession {
  accessToken: string;
  refreshToken: string | null;
  /** Epoch milliseconds. */
  expiresAt: number;
}

function storage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function readSession(): StoredSession | null {
  try {
    const raw = storage()?.getItem(SESSION_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as StoredSession;
    return typeof parsed?.accessToken === "string" ? parsed : null;
  } catch {
    return null;
  }
}

export function writeSession(session: StoredSession): void {
  try {
    storage()?.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    // Storage can be unavailable (private mode); the in-memory session still works.
  }
}

export function clearSession(): void {
  try {
    storage()?.removeItem(SESSION_KEY);
  } catch {
    // Nothing to do — the caller is signing out regardless.
  }
}

/** 30s of slack so a token is never used in the instant before it expires. */
export function isExpired(session: StoredSession, now = Date.now()): boolean {
  return session.expiresAt - 30_000 <= now;
}
